import { type CountryCode } from "libphonenumber-js";

import { SETTLEMENT_CURRENCY, type CurrencyCode } from "@trip/constants";

const currencyMap = {
  cny: { code: "cny", locales: ["zh", "zh-CN"], symbol: "¥" },
  eur: {
    code: "eur",
    locales: [
      "de-DE", // Germany
      "el-GR", // Greece
      "en-IE", // Ireland
      "es-ES", // Spain
      "fr-FR", // France
      "it-IT", // Italy
      "nl-NL", // Netherlands
      "pt-PT", // Portugal
      "fi-FI", // Finland
      "et-EE", // Estonia
      "lv-LV", // Latvia
      "lt-LT", // Lithuania
      "mt-MT", // Malta
      "el-CY", // Cyprus Greek
      "en-CY", // Cyprus English
      "sk-SK", // Slovakia
      "sl-SI", // Slovenia
      "hu-HU", // Hungary
      "ro-RO", // Romania
      "bg-BG", // Bulgaria
      "hr-HR", // Croatia
      "fr-BE", // Belgium (French)
      "nl-BE", // Belgium (Dutch)
      "de-BE", // Belgium (German)
      "de-AT", // Austria
      "fr-LU", // Luxembourg (French)
      "de-LU", // Luxembourg (German)
      "lb-LU", // Luxembourg (Luxembourgish)
    ],
    symbol: "€",
  },
  jpy: { code: "jpy", locales: ["ja", "ja-JP"], symbol: "¥" },
  krw: { code: "krw", locales: ["ko-KR"], symbol: "₩" },
  twd: { code: "twd", locales: ["zh-TW"], symbol: "NT$" },
  usd: { code: "usd", locales: ["en-US", "en"], symbol: "$" },
  gbp: { code: "gbp", locales: ["en-GB"], symbol: "£" },
  aud: { code: "aud", locales: ["en-AU"], symbol: "AU$" },
  cad: { code: "cad", locales: ["en-CA", "fr-CA"], symbol: "CAD$" },
  nzd: { code: "nzd", locales: ["en-NZ"], symbol: "NZX" },
} as const;

type CurrencyProps = (typeof currencyMap)[keyof typeof currencyMap];

export function getCurrencies(currency: CurrencyCode) {
  const props = currencyMap[currency];
  return props;
}

export function getCurrencyByCountryCode(
  countryCode: CountryCode,
): CurrencyProps["code"] {
  for (const [currency, { locales }] of Object.entries(currencyMap)) {
    if (locales.some((locale) => locale.endsWith(countryCode))) {
      return currency as CurrencyProps["code"];
    }
  }
  return "usd"; // Return null if no matching currency is found
}

export type CurrencyExchangeProps = ReturnType<typeof getCurrencies> & {
  exchangeRate: number | undefined;
};

export function formatCurrency(
  input: number,
  locale: string,
  currency: CurrencyCode,
  options?: Intl.NumberFormatOptions,
) {
  const formatter = new Intl.NumberFormat(locale.replace("_", "-"), {
    currency: currency.toUpperCase(),
    currencyDisplay: "narrowSymbol",
    style: "currency",
    maximumFractionDigits: 0,
    ...options,
  });

  let formatted = formatter.format(input);

  if (currency === "twd") {
    formatted = formatted.replace("$", "NT$");
  }

  return formatted.replace("kr.", "kr");
}

export function formatSettlementCurrency(input: number) {
  return formatCurrency(input, "en-US", SETTLEMENT_CURRENCY);
}

export function formatCurrencyMillions(
  input: number,
  locale: string,
  currency: CurrencyCode,
  options?: Intl.NumberFormatOptions,
) {
  let formattedInput = input;

  // Check if the amount is 100,000 or more
  if (input >= 100_000) {
    formattedInput = input / 1_000_000; // Convert to millions
    options = { ...options, maximumFractionDigits: 1 }; // Set maximum fraction digits for millions
  }

  const formatter = new Intl.NumberFormat(locale.replace("_", "-"), {
    currency: currency.toUpperCase(),
    currencyDisplay: "narrowSymbol",
    style: "currency",
    maximumFractionDigits: 0,
    ...options,
  });

  let formatted = formatter.format(formattedInput);

  if (currency === "twd") {
    formatted = formatted.replace("$", "NT$");
  }

  formatted = formatted.replace("kr.", "kr");

  // Add "M" for millions if the amount was converted
  if (input >= 100_000) {
    formatted += "M";
  }

  return formatted;
}
